import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addMessage, fetchSupportSessions, fetchSupportMessages, incrementUnreadMessages, resetUnreadMessages } from '../../store/Slices/supportSlice';
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import socket from '../../services/socket';
import { lightTheme, darkTheme } from '../../themes/themes';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const Support = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.data);
  const { sessions, loading } = useSelector((state) => state.support);
  const isLightTheme = useSelector((state) => state.theme.light);
  const theme = isLightTheme ? lightTheme : darkTheme;
  const [isInitialFetchDone, setIsInitialFetchDone] = useState(false);

  useEffect(() => {
    if (user?._id && !isInitialFetchDone) {
      console.log(`User ${user._id} joining WebSocket`);
      dispatch(fetchSupportSessions(user._id)).then((result) => {
        const fetchedSessions = result.payload;
        if (fetchedSessions && fetchedSessions.length > 0) {
          fetchedSessions.forEach((session) => {
            dispatch(fetchSupportMessages(session._id));
          });
        }
        setIsInitialFetchDone(true);
      });

      socket.emit('join', { user_id: user._id });
      socket.on('status', ({ status }) => {
        console.log(`WebSocket status for user ${user._id}: ${status}`);
      });
    }

    socket.on('message', (message) => {
      dispatch(addMessage(message));
    });

    socket.on('session_started', ({ session_id, user_id }) => {
      if (user_id === user._id) {
        dispatch(fetchSupportSessions(user._id));
        dispatch(fetchSupportMessages(session_id));
      }
    });

    socket.on('session_ended', ({ support_session_id }) => {
      console.log(`User received session_ended event for session ${support_session_id}`);
      setTimeout(() => {
        navigate('/support/create');
      }, 2000);
    });

    socket.on('new_support_message', ({ support_session_id, message, sender_id }) => {
      console.log(`User received new_support_message: ${message} from ${sender_id} for session ${support_session_id}`);
      dispatch(incrementUnreadMessages());
    });

    return () => {
      socket.off('message');
      socket.off('session_started');
      socket.off('session_ended');
      socket.off('new_support_message');
      socket.off('status');
    };
  }, [dispatch, user?._id, isInitialFetchDone, navigate]);

  useEffect(() => {
    console.log('Support page opened, resetting unreadMessages');
    dispatch(resetUnreadMessages());
  }, [dispatch]);

  const handleBackClick = () => {
    navigate('/support/create');
  };

  const handleOpenChat = (sessionId) => {
    navigate(`/support/session/${sessionId}`);
  };

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
      <AppBar
        position="static"
        sx={{
          bgcolor: theme.palette.background.paper,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleBackClick}
              sx={{
                mr: 2,
                color: theme.palette.brand.main,
                '&:hover': { color: '#d4a027' },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 'bold',
              }}
            >
              Support Chat
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
        <Card
          sx={{
            bgcolor: theme.palette.background.paper,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            borderRadius: '12px',
            overflow: 'hidden',
            transition: 'transform 0.2s ease-in-out',
            '&:hover': { transform: 'translateY(-4px)' },
          }}
        >
          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress sx={{ color: theme.palette.brand.main }} />
              </Box>
            ) : sessions.length === 0 ? (
              <Typography sx={{ color: theme.palette.text.secondary, textAlign: 'center', py: 2 }}>
                No active support sessions. Create a new request{' '}
                <a
                  href="/support/create"
                  style={{ color: theme.palette.brand.main, textDecoration: 'underline' }}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/support/create');
                  }}
                >
                  here
                </a>
                .
              </Typography>
            ) : (
              sessions.map((session) => (
                <Box
                  key={session._id}
                  sx={{
                    mb: 3,
                    p: 2,
                    border: `1px solid ${theme.palette.divider || '#ddd'}`,
                    borderRadius: '8px',
                    bgcolor: theme.palette.background.paper,
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
                    transition: 'box-shadow 0.2s ease-in-out',
                    '&:hover': { boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 'medium' }}>
                      {session.support_request_id?.subject || 'Support Session'}
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => handleOpenChat(session._id)}
                      sx={{
                        bgcolor: theme.palette.brand.main,
                        color: '#fff',
                        '&:hover': { bgcolor: '#d4a027' },
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        px: 2,
                        py: 0.5,
                      }}
                    >
                      Open Chat
                    </Button>
                  </Box>
                </Box>
              ))
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Support;